import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { getStoredToken, setStoredToken, clearStoredToken } from "@/lib/apiClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // Token rejected (expired/invalid). Wipe only THIS mode's token so
      // switching back to the other mode doesn't force a fresh login.
      console.warn("auth/me failed, clearing session:", err?.response?.status || err);
      clearStoredToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  // React to Live/Test mode flips — different backends have independent auth,
  // so switching mode may leave us unauthenticated on the new backend. Re-run
  // fetchMe to validate the new mode's token; if it fails, the interceptor
  // will kick us to /login for that backend.
  useEffect(() => {
    const onModeChange = () => { fetchMe(); };
    window.addEventListener("cd:mode-change", onModeChange);
    return () => window.removeEventListener("cd:mode-change", onModeChange);
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    const token = data.access_token || data.token;
    // Persist under the current mode's key. Auth is per-backend — a live
    // token 401s on preview and vice versa.
    setStoredToken(token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    try {
      const uid = user?.id;
      if (uid) localStorage.removeItem(`cd_view_mode_${uid}`);
    } catch (err) {
      // Non-fatal: localStorage may be disabled. Log so it's not silent.
      console.warn("Failed to clear stored mode preference:", err);
    }
    window.__cdActiveMode = undefined;
    // Clear both live + test tokens on explicit logout — the user is
    // stepping away, so tearing down every session is the safe default.
    clearStoredToken();
    clearStoredToken("live");
    clearStoredToken("test");
    setUser(null);
  }, [user?.id]);

  // Stable provider value — avoids re-rendering every consumer on parent re-renders.
  const value = useMemo(
    () => ({ user, loading, login, logout, refresh: fetchMe }),
    [user, loading, login, logout, fetchMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
